import nacl from 'tweetnacl';
import { TAG_DEVICE_ID } from './convKey.mjs';

/**
 * Whose keys we are encrypting to (K1, ADR-014 TOFU applied to chat).
 *
 * The prekey signature inside a bundle is made with the identity key that sits
 * in the same bundle. It proves the bundle is internally consistent and says
 * nothing about whose bundle it is: a server that answers a bundle request
 * with a device of its own passes that check every single time, sits in the
 * middle of both sessions, and both screens show an ordinary conversation.
 *
 * So the identity key is pinned on first contact and compared afterwards —
 * the same store the calls have used since ADR-014, so a person already
 * verified by a call is already trusted here.
 *
 * A device id is a hash of the identity keys, so a key cannot change under a
 * device id: an attacker does not swap a key, they ADD a device. That is why
 * the interesting state is "known person, key we have never seen" and why it
 * must block rather than warn — a message that goes out to three devices when
 * only two are trusted has already been read by the third.
 */

/**
 * Build the callback ChatSessions asks before it will start a session.
 *
 * `onRefused` is called with the device that was turned away, so the screen
 * can offer the person the one decision only they can make.
 */
export function makeBundleVerifier(tofu, { onRefused, deviceList = null } = {}) {
  return async (peerUserId, bundle) => {
    const key = bundle && bundle.identitySig;
    if (!key) return false;                       // no identity, no session

    /**
     * The account's own word, which outranks everything below.
     *
     * TOFU answers "have I seen this key before". The signed device list
     * answers "does the account say this device is theirs", and only the
     * second one is a statement by the person rather than by the server. A
     * device that is not in it is refused whether or not it is familiar —
     * being familiar only means the server showed it to us before.
     *
     * Absent (`deviceList` not wired) this falls through to the old
     * behaviour, so the check can be turned on without a flag day.
     */
    if (deviceList) {
      const verdict = await deviceList(peerUserId, bundle.deviceId, bundle.identitySig);
      if (verdict && verdict.ok === false) {
        if (onRefused) {
          onRefused({
            userId: Number(peerUserId), deviceId: bundle.deviceId, identitySig: key,
            deviceLabel: bundle.deviceLabel || null, unlisted: true, reason: verdict.reason,
            masterChanged: !!verdict.masterChanged,
          });
        }
        return false;
      }
      // Listed, with these exact keys: the account has said so under its
      // master key, which the person pinned once. That IS the confirmation —
      // asking TOFU's "new device?" on top of it is a second question with
      // no answer for the account's own devices (there is no chat with
      // oneself to confirm in), and a redundant one for a peer's. Pin by the
      // list and move on. An account with no list, or a gate that did not
      // check (`unchecked`), still gets the TOFU questions below.
      if (verdict && verdict.ok === true && !verdict.unchecked) {
        if (tofu.check(peerUserId, key) !== 'pinned') tofu.pin(peerUserId, key, bundle.deviceLabel || null);
        return true;
      }
    }

    const state = tofu.check(peerUserId, key);
    if (state === 'pinned') return true;

    if (state === 'tofu-new') {
      // First contact with this person on this device. There is nothing to
      // compare against, and refusing would mean nobody can ever start — this
      // is the trust-on-first-use bet, and it is the same bet the calls make.
      tofu.pin(peerUserId, key, bundle.deviceLabel || null);
      return true;
    }

    // 'new-device': a key we have never seen from somebody we know. It is a
    // new phone far more often than an attack — and it is indistinguishable
    // from an attack, which is exactly why a person has to say which it is.
    if (onRefused) {
      onRefused({
        userId: Number(peerUserId),
        deviceId: bundle.deviceId,
        identitySig: key,
        deviceLabel: bundle.deviceLabel || null,
      });
    }
    return false;
  };
}

/** The person said this device is theirs. Pin it; the next send goes through. */
export function trustDevice(tofu, peerUserId, identitySig, deviceLabel) {
  return tofu.pin(peerUserId, identitySig, deviceLabel || null);
}

/**
 * A short, comparable fingerprint of a device identity, for the screen.
 *
 * Not a safety number: it is one device's key, shown so two people can read
 * the same eight characters to each other over a channel the server does not
 * own. Full verification lives with the call SAS (ADR-014).
 */
export function shortFingerprint(identitySigB64) {
  if (!identitySigB64) return '';
  const clean = String(identitySigB64).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return clean.slice(0, 4) + ' ' + clean.slice(4, 8);
}

/**
 * The same question, asked of a handshake instead of a bundle.
 *
 * `makeBundleVerifier` guards what we ENCRYPT TO. This guards what we OPEN,
 * and it took an outside audit to notice they are different questions. A
 * prekey message carries the initiator's identity DH key on the wire, X3DH is
 * derived straight from it, and who sent it comes from a row the server owns.
 * Nothing tied the two together: a hostile server generates an identity of its
 * own, builds a valid handshake to us, files it under somebody else's name,
 * and we open it and put their name on it. No key is broken. The initiator's
 * identity was simply never compared to anything.
 *
 * The comparison is possible because a device id is a hash of BOTH identity
 * keys. So: look up the device that claims to have written, check its bundle
 * really hashes to that device id, check the DH key on the wire is the one in
 * that bundle, and check the signing key against the pin we already hold —
 * the same pin the calls use (ADR-014), so somebody verified by voice is
 * already verified here.
 *
 * Deliberately narrow: it refuses only when a pin EXISTS and disagrees. With
 * no pin this is first contact and TOFU applies, exactly as on the send side;
 * if the device list cannot be fetched we do not know, and refusing on "do not
 * know" would make an unreachable server look like an impostor and lose real
 * messages. That is the same trust-on-first-use bet made everywhere else, and
 * it is worth being exact that it is a bet.
 */
/** No base64 string contains a NUL, so this is nobody's key — see below. */
const NOT_A_KEY = '\u0000';

export function makeIncomingVerifier(tofu, { deviceIdOf, listDevices, onImpostor, deviceList = null } = {}) {
  return async (peerUserId, peerDeviceId, identityDhPub, identitySigPub = null) => {
    if (!peerDeviceId || !identityDhPub) return { ok: true, checked: false };

    // Same question, same precedence: a handshake from a device the account
    // never signed for is refused before anything else is considered.
    if (deviceList) {
      // With the signing key from the wire, so the account's own signed list
      // answers about the KEYS and not only the id. Passing null here asked
      // half the question: a device id the account signed for once would stay
      // a slot the server could refill (deviceListCache.checkDevice).
      const verdict = await deviceList(peerUserId, peerDeviceId,
        identitySigPub ? b64OfDh(identitySigPub) : null);
      if (verdict && verdict.ok === false) {
        return refuse(onImpostor, peerUserId, peerDeviceId, verdict.reason, verdict);
      }
    }

    let devices;
    try {
      const r = await listDevices(peerUserId);
      devices = (r && r.devices) || [];
    } catch (e) {
      // The v2 envelope carries the signing key itself, so an unreachable
      // server no longer means an unaskable question: a pin that EXISTS and
      // disagrees refuses even offline. No pin still means "do not know" —
      // refusing on that would make every outage look like an impostor.
      if (identitySigPub) {
        const state = tofu.check(peerUserId, b64OfDh(identitySigPub));
        if (state === 'new-device') {
          return refuse(onImpostor, peerUserId, peerDeviceId,
            'рукопожатие подписано ключом, которого у этого человека не было, — а список устройств недоступен');
        }
        if (state === 'pinned') return { ok: true, checked: true, why: 'pinned key, device list unavailable' };
      }
      return { ok: true, checked: false, why: 'device list unavailable' };
    }
    const device = devices.find(d => d.deviceId === peerDeviceId);
    if (!device || !device.identitySig || !device.identityDh) {
      // The server routed a handshake from a device its own key listing does
      // not contain. Innocent readings exist — a revoked device, or a bundle
      // the server dropped over its ten-device limit — and a hostile one is
      // identical in shape: quote a real device id out of the signed list,
      // sign the handshake with your own key, and simply omit that device
      // from the listing.
      //
      // This branch used to ask `tofu.check(peerUserId, null) === 'pinned'`,
      // and null is pinned for nobody: it was always false, so the four
      // checks below were skipped whole and the message opened under the
      // claimed name. The question it meant to ask is answerable, because the
      // signing key is ON THE WIRE — the same key the offline branch above
      // uses. So ask the pin about that key instead of about null.
      if (identitySigPub) {
        const state = tofu.check(peerUserId, b64OfDh(identitySigPub));
        if (state === 'pinned') return { ok: true, checked: true, why: 'pinned key, bundle not listed' };
        if (state === 'new-device') {
          return refuse(onImpostor, peerUserId, peerDeviceId,
            'сервер не знает устройства, от имени которого пришло рукопожатие');
        }
        return { ok: true, checked: false, why: 'device not listed, nothing pinned yet' };
      }
      // A v1 envelope carries no signing key. Then knowing this person at all
      // is the only signal left, and the server contradicting itself about
      // somebody we know is not something to open quietly. NOT_A_KEY asks
      // that question through `check`, which every pin store has: it answers
      // 'new-device' when the person is known and 'tofu-new' when they are
      // not — and it can never be somebody's key, because base64 has no NUL.
      return tofu.check(peerUserId, NOT_A_KEY) === 'new-device'
        ? refuse(onImpostor, peerUserId, peerDeviceId, 'сервер не знает устройства, от имени которого пришло рукопожатие')
        : { ok: true, checked: false, why: 'device not listed' };
    }

    // The device id is a hash of both identity keys, so a bundle that does not
    // hash to the id it is filed under is the server contradicting itself.
    if (deviceIdOf && deviceIdOf(device.identitySig, device.identityDh) !== peerDeviceId) {
      return refuse(onImpostor, peerUserId, peerDeviceId, 'ключи устройства не сходятся с его идентификатором');
    }

    // The DH key the handshake was actually built from must be that device's.
    if (b64OfDh(identityDhPub) !== device.identityDh) {
      return refuse(onImpostor, peerUserId, peerDeviceId, 'рукопожатие построено на ключе, который этому устройству не принадлежит');
    }

    // And so must the key that SIGNED it. Without this line a server that
    // lists the honest bundle but routes an attacker's envelope under it
    // would pass: the DH key above can be copied out of the public bundle,
    // the signature cannot — it needs the secret half. This is the check
    // NEW-SEC-01 is about.
    if (identitySigPub && b64OfDh(identitySigPub) !== device.identitySig) {
      return refuse(onImpostor, peerUserId, peerDeviceId, 'рукопожатие подписано ключом, который этому устройству не принадлежит');
    }

    const state = tofu.check(peerUserId, device.identitySig);
    if (state === 'pinned') return { ok: true, checked: true };
    if (state === 'tofu-new') {
      tofu.pin(peerUserId, device.identitySig, device.deviceLabel || null);
      return { ok: true, checked: true, firstContact: true };
    }
    // 'new-device': a signing key we have never seen from somebody we know.
    // On the send side a person decides. Here there is nobody to ask at the
    // moment a message arrives, and opening it would print their name over an
    // unverified key — so it waits, and the trust screen is where it is
    // resolved.
    return refuse(onImpostor, peerUserId, peerDeviceId,
      'сообщение пришло с устройства, которое вы ещё не подтверждали');
  };
}

/**
 * Say no, and say enough about WHY for a screen to offer the right answer.
 *
 * `masterChanged` used to be dropped here. The device-list gate knew whether
 * the account had been re-signed by a different master key; by the time the
 * settings screen saw the refusal that fact was gone, so every refusal — a
 * device missing from the list, keys that do not match, an unconfirmed device
 * — was offered the same button: «принять новый ключ аккаунта», which erases
 * the pinned master. Right medicine for one cause, wrong for the rest.
 */
function refuse(onImpostor, userId, deviceId, reason, verdict = null) {
  const notice = {
    userId: Number(userId), deviceId, reason,
    masterChanged: !!(verdict && verdict.masterChanged),
  };
  if (onImpostor) onImpostor(notice);
  return { ok: false, reason, masterChanged: notice.masterChanged };
}

/**
 * device_id = SHA-512("VoxioxDeviceIdV1" || IK_sig || IK_dh)[0..16), base64url.
 *
 * The same recipe the server uses (routes/keys.js). Recomputing it here is
 * what turns a device id from a label the server hands out into a claim the
 * client can check: keys that do not hash to the id they are filed under are
 * the server contradicting itself in one request.
 */
export function deviceIdOf(identitySigB64, identityDhB64) {
  const tag = new TextEncoder().encode(TAG_DEVICE_ID);
  const sig = fromB64(identitySigB64), dh = fromB64(identityDhB64);
  if (!sig || !dh || sig.length !== 32 || dh.length !== 32) return null;
  const input = new Uint8Array(tag.length + 64);
  input.set(tag, 0); input.set(sig, tag.length); input.set(dh, tag.length + 32);
  const h = nacl.hash(input).slice(0, 16);
  let s = '';
  for (let i = 0; i < h.length; i++) s += String.fromCharCode(h[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64(str) {
  if (typeof str !== 'string') return null;
  try {
    const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/'));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch (e) { return null; }
}

/** The wire carries raw bytes; a bundle carries base64. Compare in one form. */
function b64OfDh(u8) {
  if (typeof u8 === 'string') return u8;
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}
